import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { canManageAccess, canManageEntry, isOwner } from '@budgetmonkey/shared'
import { Badge, Button, Card, ErrorNote, Input } from '@/components/ui'
import { formatDate } from '@/lib/dates'
import {
  addAllowedUser,
  fetchAllowedUsers,
  removeAllowedUser,
  setAllowedUserAdmin,
  type AllowedUser,
} from './accessApi'

/**
 * Who else may use BudgetMonkey — REQUIREMENTS.md §9.
 *
 * Shown only to the owner, whose address is the one hardcoded in the rules.
 * Everyone else is a document, so this panel is how access is granted rather
 * than a code change and a deploy of two rules files.
 *
 * Each person's data stays entirely their own: every document carries a
 * `userId` and the rules refuse a read or write across users, which the rules
 * tests prove with two allowlisted accounts rather than only with a stranger.
 */
export function AccessCard({ email }: { email: string }) {
  const client = useQueryClient()
  const [input, setInput] = useState('')
  const [rejected, setRejected] = useState<string>()
  const [confirmingRemoval, setConfirmingRemoval] = useState<string>()

  const { data: allowed = [], isLoading, error } = useQuery({
    queryKey: ['allowedUsers'],
    queryFn: fetchAllowedUsers,
  })

  const add = useMutation({
    mutationFn: (address: string) => addAllowedUser(address, email),
    onSuccess: async () => {
      setInput('')
      setRejected(undefined)
      await client.invalidateQueries({ queryKey: ['allowedUsers'] })
    },
    onError: (cause: Error) => setRejected(cause.message),
  })

  const remove = useMutation({
    mutationFn: (address: string) => removeAllowedUser(address),
    onSuccess: async () => {
      setConfirmingRemoval(undefined)
      await client.invalidateQueries({ queryKey: ['allowedUsers'] })
    },
  })

  const setAdmin = useMutation({
    mutationFn: ({ user, admin }: { user: AllowedUser; admin: boolean }) =>
      setAllowedUserAdmin(user, admin, email),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['allowedUsers'] })
    },
    onError: (cause: Error) => setRejected(cause.message),
  })

  const owner = isOwner(email)

  // Not a security boundary — the rules refuse a write from anyone else. This
  // only avoids showing a panel whose every action would fail.
  //
  // Admins see it too, which is the point of the flag: a colleague's access
  // should not wait on one person being reachable. Granting admin stays the
  // owner's, here and in the rules.
  if (!canManageAccess(email, allowed)) return null

  return (
    <Card className="space-y-4">
      <div>
        <h2 className="font-medium text-slate-900">Who can use Budget Monkey</h2>
        <p className="mt-1 text-sm text-slate-600">
          District colleagues you add here sign in with their own Google
          account and keep their own budget codes, receipts and packets. Nobody
          can see anyone else's — not even you.
        </p>
      </div>

      {error != null && <ErrorNote>Couldn't load the list: {error.message}</ErrorNote>}

      <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
        <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">{email}</span>
            <span className="text-xs text-slate-500">
              {owner
                ? 'The owner — always has access, and the only one who can appoint an administrator'
                : 'You — an administrator, so you can add and remove people here'}
            </span>
          </span>
        </li>

        {isLoading ? (
          <li className="px-4 py-3 text-sm text-slate-500" role="status">
            Loading…
          </li>
        ) : (
          allowed.map((entry) => (
            <AllowedRow
              key={entry.email}
              entry={entry}
              confirming={confirmingRemoval === entry.email}
              pending={remove.isPending}
              // An admin manages ordinary users; a peer is the owner's to
              // settle. The rules say the same, so this only avoids offering
              // a button that would fail.
              manageable={canManageEntry(email, entry, allowed)}
              canAppoint={owner}
              appointing={setAdmin.isPending}
              onSetAdmin={(admin) => setAdmin.mutate({ user: entry, admin })}
              onAskRemove={() => setConfirmingRemoval(entry.email)}
              onCancel={() => setConfirmingRemoval(undefined)}
              onRemove={() => remove.mutate(entry.email)}
            />
          ))
        )}
      </ul>

      <form
        className="flex flex-wrap items-start gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          add.mutate(input)
        }}
      >
        <span className="min-w-56 flex-1">
          <label className="sr-only" htmlFor="add-user">
            District email address
          </label>
          <Input
            id="add-user"
            type="email"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="colleague@southfayette.org"
          />
        </span>
        <Button variant="primary" type="submit" disabled={add.isPending}>
          {add.isPending ? 'Adding…' : 'Give access'}
        </Button>
      </form>

      {rejected !== undefined && <ErrorNote>{rejected}</ErrorNote>}

      <p className="text-xs text-slate-500">
        They sign in at this address with their district Google account. On
        first sign-in they add their own budget codes — nothing of yours is
        copied to them, and nothing of theirs reaches you.
      </p>
    </Card>
  )
}

function AllowedRow({
  entry,
  confirming,
  pending,
  manageable,
  canAppoint,
  appointing,
  onSetAdmin,
  onAskRemove,
  onCancel,
  onRemove,
}: {
  entry: AllowedUser
  confirming: boolean
  pending: boolean
  manageable: boolean
  canAppoint: boolean
  appointing: boolean
  onSetAdmin: (admin: boolean) => void
  onAskRemove: () => void
  onCancel: () => void
  onRemove: () => void
}) {
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
      <span className="min-w-0">
        <span className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium">{entry.email}</span>
          {entry.admin && <Badge>Administrator</Badge>}
        </span>
        <span className="text-xs text-slate-500">
          {entry.addedAt === '' ? 'Added' : `Added ${formatDate(entry.addedAt)}`}
          {entry.admin && ' · can add and remove people'}
        </span>
      </span>

      {/* Two-step: this is someone's access to their own records, and the
          button sits in a list they might be scrolling. */}
      {confirming ? (
        <span className="flex items-center gap-2">
          <Button variant="danger" onClick={onRemove} disabled={pending}>
            {pending ? 'Removing…' : 'Remove access'}
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Keep
          </Button>
        </span>
      ) : (
        <span className="flex flex-wrap items-center gap-2">
          {canAppoint && (
            <Button
              variant="ghost"
              disabled={appointing}
              onClick={() => onSetAdmin(!entry.admin)}
            >
              {entry.admin ? 'Remove admin' : 'Make admin'}
            </Button>
          )}
          {manageable ? (
            <Button variant="ghost" onClick={onAskRemove}>
              Remove
            </Button>
          ) : (
            <span className="text-xs text-slate-500">
              Only the owner can remove an administrator
            </span>
          )}
        </span>
      )}
    </li>
  )
}
